<template>
  <app-layout title="Payment Details">
    <form @submit.prevent="openSubmitModal">
      <!-- Sticky header -->
      <div class="border-b border-gray bg-white sticky top-0 z-10 p-2 lg:px-8">
        <div class="flex justify-between items-center">
          <div class="font-semibold text-lg">
            Payment Details
          </div>

          <div class="flex items-center gap-x-2">
            <jet-button
              v-if="canAccessPaymentDetails"
              type="submit"
              class="h-8 leading-none"
              :disabled="form.processing || !canSubmit || !canSubmitByStatus"
            >
              Submit
            </jet-button>
          </div>
        </div>
      </div>

      <div class="px-8 pt-4">
        <div
          v-if="$page.props.errors.payment"
          class="rounded px-4 py-3 text-sm border border-red-300 bg-red-50 text-red-800"
        >
          <div class="font-semibold mb-1">Payment could not be verified</div>
          <div>{{ $page.props.errors.payment }}</div>
        </div>

        <div
          v-if="onboardingStatus === 'customer_rejected'"
          class="rounded px-4 py-3 text-sm border border-red-300 bg-red-50 text-red-800"
        >
          <div class="font-semibold mb-1">Your application has been rejected.</div>
          <div class="text-xs mt-0.5">Please contact your sales representative for further details.</div>
        </div>

        <div
          v-else-if="onboardingStatus === 'payment_details_approved'"
          class="rounded px-4 py-3 text-sm border border-green-300 bg-green-50 text-green-800"
        >
          <div class="font-semibold mb-1">These payment details have been approved.</div>
          <div class="text-xs mt-0.5">No further action required for this step. Your account will be activated shortly.</div>
        </div>

        <div
          v-else-if="lastSubmittedAt && !isDirty"
          :class="[
            'rounded px-4 py-3 text-sm border',
            changesRequested
              ? 'border-red-300 bg-red-50 text-red-800'
              : 'border-gray-200 bg-gray-50 text-gray-700'
          ]"
        >
          <div v-if="!changesRequested">
            <div class="font-medium text-gray-800">These details have already been submitted.</div>
            <div class="text-xs text-gray-600 mt-0.5">Submitted: {{ formattedSubmittedAt }}</div>
          </div>

          <div v-else>
            <div class="font-semibold mb-1">The following corrections are required for your payment details to be approved:</div>
            <div class="text-sm">{{ rejectedReason }}</div>
            <br />
            <div class="text-xs mt-0.5">Please contact your sales representative for further details.</div>
          </div>
        </div>

        <div
          v-else-if="lastSubmittedAt && isDirty && canSubmitByStatus"
          class="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          You’ve made changes — please re-submit to update.
        </div>
      </div>

      <!-- Page body -->
      <div class="px-8 py-6">
        <div
          v-if="!canAccessPaymentDetails"
          class="rounded border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
        >
          <div class="font-semibold">Payment details not available yet</div>
          <div class="mt-1 text-amber-800">
            {{ accessBlockedMessage }}
          </div>
        </div>

        <template v-else>
          <!-- CREDIT CARD VIEW -->
          <div v-if="isCreditCard" class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Billing Address -->
            <section class="border rounded p-4">
              <div class="font-semibold text-gray-800 mb-4">Billing Address</div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <jet-label for="first_name" value="First Name" />
                  <jet-input id="first_name" type="text" class="block w-full" v-model="form.first_name" />
                  <jet-input-error :message="$page.props.errors.first_name" class="mt-1" />
                </div>

                <div>
                  <jet-label for="last_name" value="Last Name" />
                  <jet-input id="last_name" type="text" class="block w-full" v-model="form.last_name" />
                  <jet-input-error :message="$page.props.errors.last_name" class="mt-1" />
                </div>
              </div>

              <div class="mt-4">
                <jet-label for="phone" value="Phone" />
                <jet-input id="phone" type="tel" class="block w-full" v-model="form.phone" />
                <jet-input-error :message="$page.props.errors.phone" class="mt-1" />
              </div>

              <div class="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <jet-label for="street_number" value="Street Number" />
                  <jet-input id="street_number" type="text" class="block w-full" v-model="form.street_number" />
                  <jet-input-error :message="$page.props.errors.street_number" class="mt-1" />
                </div>

                <div>
                  <jet-label for="street_name" value="Street Name" />
                  <jet-input id="street_name" type="text" class="block w-full" v-model="form.street_name" />
                  <jet-input-error :message="$page.props.errors.street_name" class="mt-1" />
                </div>

                <div>
                  <jet-label for="postal_code" value="Postal Code" />
                  <jet-input id="postal_code" type="text" class="block w-full" v-model="form.postal_code" />
                  <jet-input-error :message="$page.props.errors.postal_code" class="mt-1" />
                  </div>
              </div>

              <div class="mt-4">
                <jet-label for="city" value="City" />
                <jet-input id="city" type="text" class="block w-full" v-model="form.city" />
                <jet-input-error :message="$page.props.errors.city" class="mt-1" />
              </div>

              <div class="mt-4">
                <jet-label for="province" value="Province" />
                <multiselect
                  v-model="form.province"
                  :options="provinceOptions"
                  :classes="{ singleLabel: 'w-full px-2' }"
                  class="multi-lg"
                  placeholder="Select province"
                />
                <jet-input-error :message="$page.props.errors.province" class="mt-1" />
              </div>
            </section>

            <!-- Card Details -->
            <section class="border rounded p-4">
              <div class="font-semibold text-gray-800 mb-1">Card Details</div>

              <div class="mb-4">
                <jet-label for="card_number" value="Card Number" />
                <jet-input
                  id="card_number"
                  type="text"
                  class="block w-full"
                  v-model="form.card_number"
                  placeholder="•••• •••• •••• ••••"
                />
                <jet-input-error :message="$page.props.errors.card_number" class="mt-1" />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <jet-label for="expiry" value="Expiry (MMYY)" />
                  <jet-input id="expiry" type="text" class="block w-full" v-model="form.expiry" placeholder="0127" />
                  <jet-input-error :message="$page.props.errors.expiry" class="mt-1" />
                </div>

                <div>
                  <jet-label for="cvd" value="CVC" />
                  <jet-input id="cvd" type="text" class="block w-full" v-model="form.cvd" placeholder="123" />
                  <jet-input-error :message="$page.props.errors.cvd" class="mt-1" />
                </div>
              </div>
            </section>
          </div>

          <!-- NON CREDIT CARD VIEW -->
          <div v-else class="border rounded p-4">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <!-- General -->
              <section>
                <div class="font-semibold text-gray-800 mb-4">General</div>

                <div class="mb-4">
                  <jet-label for="account_holder_name" value="Account Holder Name" />
                  <jet-input
                    id="account_holder_name"
                    type="text"
                    class="block w-full"
                    v-model="form.account_holder_name"
                  />
                  <jet-input-error :message="$page.props.errors.account_holder_name" class="mt-1" />
                </div>

                <div class="mb-4">
                  <jet-label for="bank_name" value="Bank Name" />
                  <jet-input id="bank_name" type="text" class="block w-full" v-model="form.bank_name" />
                  <jet-input-error :message="$page.props.errors.bank_name" class="mt-1" />
                </div>

                <div class="mb-1">
                  <jet-label for="account_type" value="Account Type" />
                  <jet-input id="account_type" type="text" class="block w-full" v-model="form.account_type" />
                  <jet-input-error :message="$page.props.errors.account_type" class="mt-1" />
                </div>
              </section>

              <!-- Routing Details -->
              <section>
                <div class="font-semibold text-gray-800 mb-4">Routing Details</div>

                <div class="mb-4">
                  <jet-label for="account_number" value="Account Number" />
                  <jet-input id="account_number" type="text" class="block w-full" v-model="form.account_number" />
                  <jet-input-error :message="$page.props.errors.account_number" class="mt-1" />
                </div>

                <div class="mb-4">
                  <jet-label for="transit_number" value="Transit Number" />
                  <jet-input id="transit_number" type="text" class="block w-full" v-model="form.transit_number" />
                  <jet-input-error :message="$page.props.errors.transit_number" class="mt-1" />
                </div>

                <div class="mb-1">
                  <jet-label for="institution_number" value="Institution Number" />
                  <jet-input
                    id="institution_number"
                    type="text"
                    class="block w-full"
                    v-model="form.institution_number"
                  />
                  <jet-input-error :message="$page.props.errors.institution_number" class="mt-1" />
                </div>
              </section>
            </div>

            <!-- DEFT file upload-->
            <section v-if="isDeft" class="mt-6 border-t pt-6">
              <div class="font-semibold text-gray-800 mb-4">Banking documents (DEFT)</div>
              <p class="text-sm text-gray-600 mb-4">Upload two PDFs: void cheque or bank letter, and signed authorization.</p>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <jet-label for="deft_bank_details_file" value="Void cheque or bank letter (PDF)" />
                  <div class="min-h-[12rem] mt-1">
                    <template v-if="deftBankDetailsFileName && showPersistedDeftFiles">
                      <div
                        class="flex flex-col items-center justify-center w-full h-full min-h-[12rem] border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 px-3 py-4"
                      >
                        <p class="text-sm text-gray-700 font-semibold text-center break-all">{{ deftBankDetailsFileName }}</p>
                      </div>
                    </template>
                    <template v-else-if="!form.deft_bank_details_file">
                      <file-input-label>
                        <input
                          ref="deftBankDetailsInput"
                          id="deft_bank_details_file"
                          type="file"
                          accept=".pdf,application/pdf"
                          class="hidden"
                          @change="onDeftBankDetailsFileChange"
                        />
                      </file-input-label>
                    </template>
                    <div
                      v-else
                      class="flex flex-col items-center justify-center w-full h-full min-h-[12rem] border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 px-3 py-4"
                    >
                      <p class="text-sm text-gray-700 font-semibold text-center break-all">{{ form.deft_bank_details_file.name }}</p>
                      <button
                        type="button"
                        class="mt-2 flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800 focus:outline-none"
                        @click="clearDeftBankDetailsFile"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    </div>
                  </div>
                  <jet-input-error :message="$page.props.errors.deft_bank_details_file" class="mt-1" />
                </div>

                <div>
                  <jet-label for="deft_authorization_file" value="Signed authorization (PDF)" />
                  <div class="min-h-[12rem] mt-1">
                    <template v-if="deftAuthorizationFileName && showPersistedDeftFiles">
                      <div
                        class="flex flex-col items-center justify-center w-full h-full min-h-[12rem] border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 px-3 py-4"
                      >
                        <p class="text-sm text-gray-700 font-semibold text-center break-all">{{ deftAuthorizationFileName }}</p>
                      </div>
                    </template>
                    <template v-else-if="!form.deft_authorization_file">
                      <file-input-label>
                        <input
                          ref="deftAuthorizationInput"
                          id="deft_authorization_file"
                          type="file"
                          accept=".pdf,application/pdf"
                          class="hidden"
                          @change="onDeftAuthorizationFileChange"
                        />
                      </file-input-label>
                    </template>
                    <div
                      v-else
                      class="flex flex-col items-center justify-center w-full h-full min-h-[12rem] border-2 border-gray-300 border-dashed rounded-lg bg-gray-50 px-3 py-4"
                    >
                      <p class="text-sm text-gray-700 font-semibold text-center break-all">{{ form.deft_authorization_file.name }}</p>
                      <button
                        type="button"
                        class="mt-2 flex items-center gap-1.5 text-sm text-red-600 hover:text-red-800 focus:outline-none"
                        @click="clearDeftAuthorizationFile"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Remove
                      </button>
                    </div>
                  </div>
                  <jet-input-error :message="$page.props.errors.deft_authorization_file" class="mt-1" />
                </div>
              </div>
            </section>
          </div>
        </template>
      </div>
    </form>

    <jet-confirmation-modal :show="showSubmitConfirmModal" @close="closeSubmitModal">
      <template #title>
        Confirm submission
      </template>

      <template #content>
        <div class="text-sm text-gray-700">
          This information cannot be modified after submission. Are you sure you want to submit?
        </div>
      </template>

      <template #footer>
        <jet-button :disabled="form.processing" @click="confirmSubmit">
          Submit
        </jet-button>
        <jet-tertiary-button class="ml-2" :disabled="form.processing" @click="closeSubmitModal">
          Cancel
        </jet-tertiary-button>
      </template>
    </jet-confirmation-modal>
  </app-layout>
</template>

<script>
import { useForm } from '@inertiajs/inertia-vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import JetButton from '@/Jetstream/Button'
import JetTertiaryButton from '@/Jetstream/TertiaryButton'
import JetConfirmationModal from '@/Jetstream/ConfirmationModal'
import JetLabel from '@/Jetstream/Label'
import JetInput from '@/Jetstream/Input'
import JetInputError from '@/Jetstream/InputError.vue'
import FileInputLabel from '@/Jetstream/FileInputLabel'
import Multiselect from '@vueform/multiselect'

export default {
  props: [
    'customer',
    'customerData',
    'onboardingStatus',
    'rejectedReason',
    'lastSubmittedAt',
    'submitRoute',
    'deftBankDetailsFileName',
    'deftAuthorizationFileName',
  ],

  components: {
    AppLayout,
    JetButton,
    JetTertiaryButton,
    JetConfirmationModal,
    JetLabel,
    JetInput,
    JetInputError,
    FileInputLabel,
    Multiselect,
  },

  setup(props) {
    const form = useForm({
      customer_id: props.customer?.id ?? null,

      payment_method: props.customerData?.payment_method ?? null,

      first_name: props.customerData?.first_name ?? null,
      last_name: props.customerData?.last_name ?? null,
      phone: props.customerData?.phone ?? null,
      street_number: props.customerData?.street_number ?? null,
      street_name: props.customerData?.street_name ?? null,
      postal_code: props.customerData?.postal_code ?? null,
      city: props.customerData?.city ?? null,
      province: props.customerData?.province ?? null,

      card_number: props.customerData?.card_number ?? null,
      expiry: props.customerData?.expiry ?? null,
      cvd: props.customerData?.cvd ?? null,

      account_holder_name: props.customerData?.account_holder_name ?? null,
      bank_name: props.customerData?.bank_name ?? null,
      account_type: props.customerData?.account_type ?? null,

      account_number: props.customerData?.account_number ?? null,
      transit_number: props.customerData?.transit_number ?? null,
      institution_number: props.customerData?.institution_number ?? null,

      deft_bank_details_file: null,
      deft_authorization_file: null,
    })

    const initial = JSON.stringify(form.data())
    return { form, initial }
  },

  data() {
    return {
      showSubmitConfirmModal: false,
      provinceOptions: [
        'Ontario',
        'Manitoba',
        'Saskatchewan',
        'British Columbia',
        'Yukon',
        'Northwest Territories',
        'Nunavut',
        'Quebec',
        'Nova Scotia',
        'New Brunswick',
        'Newfoundland',
        'Prince Edward Island',
      ],
    }
  },

  methods: {
    openSubmitModal() {
      if (this.form.processing || !this.canSubmit || !this.canSubmitByStatus) return
      this.showSubmitConfirmModal = true
    },
    closeSubmitModal() {
      this.showSubmitConfirmModal = false
    },
    confirmSubmit() {
      if (this.form.processing) return
      this.showSubmitConfirmModal = false
      this.submit()
    },
    submit() {
      this.form.post(this.submitRoute, {
        preserveScroll: false,
        forceFormData: true,
        onSuccess: () => {
          this.initial = JSON.stringify(this.form.data())
          window.scrollTo(0, 0)
        },
      })
    },

    onDeftBankDetailsFileChange(event) {
      const file = event.target.files?.[0]
      this.form.deft_bank_details_file = file || null
    },

    onDeftAuthorizationFileChange(event) {
      const file = event.target.files?.[0]
      this.form.deft_authorization_file = file || null
    },

    clearDeftBankDetailsFile() {
      this.form.deft_bank_details_file = null
      this.$nextTick(() => {
        const el = this.$refs.deftBankDetailsInput
        if (el) el.value = ''
      })
    },

    clearDeftAuthorizationFile() {
      this.form.deft_authorization_file = null
      this.$nextTick(() => {
        const el = this.$refs.deftAuthorizationInput
        if (el) el.value = ''
      })
    },
  },

  computed: {
    isCreditCard() {
      return ['credit_card', 'credit_card_manual'].includes(this.form.payment_method)
    },

    isDeft() {
      return this.form.payment_method === 'deft'
    },

    changesRequested() {
      return this.onboardingStatus === 'payment_details_correction_required'
    },

    canSubmit() {
      if (this.isCreditCard) {
        return !!this.form.first_name && !!this.form.last_name && !!this.form.card_number && !!this.form.expiry && !!this.form.cvd
      }

      const banking = !!this.form.account_holder_name && !!this.form.account_number && !!this.form.transit_number && !!this.form.institution_number
      if (this.isDeft) {
        const hasBothFiles = !!this.form.deft_bank_details_file && !!this.form.deft_authorization_file
        return banking && hasBothFiles
      }
      return banking
    },

    showPersistedDeftFiles() {
      return ['payment_details_submitted', 'payment_details_approved'].includes(this.onboardingStatus)
    },

    isDirty() {
      return JSON.stringify(this.form.data()) !== this.initial
    },

    formattedSubmittedAt() {
      if (!this.lastSubmittedAt) return ''
      const d = new Date(this.lastSubmittedAt)
      return isNaN(d.getTime()) ? this.lastSubmittedAt : d.toLocaleString()
    },

    canAccessPaymentDetails() {
      return new Set([
        'business_details_approved',
        'payment_details_submitted',
        'payment_details_correction_required',
        'payment_details_approved',
        'customer_rejected',
      ]).has(this.onboardingStatus)
    },

    // Only allow submission when business details are approved (first time) or payment details were rejected
    canSubmitByStatus() {
      return ['business_details_approved', 'payment_details_correction_required'].includes(this.onboardingStatus)
    },

    accessBlockedMessage() {
      if (this.onboardingStatus === 'business_details_submitted') {
        return 'Your business details are still pending approval. Once approved, you can submit payment details.'
      }

      return 'Your business details must be approved before you can submit payment details.'
    },
  },
}
</script>

<style scoped>
.multiselect.is-active {
    box-shadow: 0 0 0 2px rgba(133, 199, 30, 0.4) !important;
}
</style>
